import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { allowedLegendPatch, isPersistableLegend } from "./legendAdapter";
import { useOverlayScrollLock } from "./useOverlayScrollLock";
import type {
  BoardLegendItem,
  BoardLegendLink,
  BoardLegendPatch,
  LegendLinkKind,
  LegendVisualStyle,
} from "./legendTypes";
import { LEGEND_LINK_KINDS } from "./legendTypes";

const LINK_KIND_LABEL: Record<LegendLinkKind, string> = {
  feishu_wiki: "飞书 Wiki",
  feishu_drive: "飞书云盘",
  feishu_doc: "飞书文档",
  public_url: "公开链接",
  ops_path: "ops 路径",
  x_account: "X 账号",
};

const fieldClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50";

type Props = {
  item: BoardLegendItem;
  onClose: () => void;
  onSave: (item: BoardLegendItem, patch: BoardLegendPatch) => Promise<unknown>;
  onOpenFlow: () => void;
};

export function LegendEditDrawer({ item, onClose, onSave, onOpenFlow }: Props) {
  useOverlayScrollLock(true);
  const persistable = isPersistableLegend(item);
  const [label, setLabel] = useState(item.label);
  const [description, setDescription] = useState(item.description ?? "");
  const [visualStyle, setVisualStyle] = useState<LegendVisualStyle>(
    item.visual_style,
  );
  const [sortOrder, setSortOrder] = useState(String(item.sort_order));
  const [showInLegend, setShowInLegend] = useState(item.show_in_legend);
  const [barHex, setBarHex] = useState(item.bar_hex ?? "");
  const [barTextHex, setBarTextHex] = useState(item.bar_text_hex ?? "");
  const [outlineHex, setOutlineHex] = useState(item.outline_hex ?? "");
  const [outlineTextHex, setOutlineTextHex] = useState(
    item.outline_text_hex ?? "",
  );
  const [softDotHex, setSoftDotHex] = useState(item.soft_dot_hex ?? "");
  const [softChipBg, setSoftChipBg] = useState(item.soft_chip_bg ?? "");
  const [softChipText, setSoftChipText] = useState(item.soft_chip_text ?? "");
  const [macAccountPath, setMacAccountPath] = useState(
    item.mac_account_path ?? "",
  );
  const [links, setLinks] = useState<BoardLegendLink[]>(item.links);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!persistable) return;
    const patch = allowedLegendPatch({
      label,
      description,
      visual_style: visualStyle,
      sort_order: Number(sortOrder),
      show_in_legend: showInLegend,
      bar_hex: barHex,
      bar_text_hex: barTextHex,
      outline_hex: outlineHex,
      outline_text_hex: outlineTextHex,
      soft_dot_hex: softDotHex,
      soft_chip_bg: softChipBg,
      soft_chip_text: softChipText,
      links,
      mac_account_path: macAccountPath,
    });
    if (!patch.label) {
      setError("名称不能为空");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(item, patch);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "保存失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(20,24,28,0.55)]"
        onClick={onClose}
      />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="founder-legend-title"
        className="fixed top-1/2 left-1/2 z-10 flex max-h-[min(86vh,720px)] w-[min(480px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-[#e6e6e4] bg-white text-card-foreground shadow-md max-md:top-auto max-md:right-0 max-md:bottom-0 max-md:left-0 max-md:max-h-[80vh] max-md:w-full max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-t-[12px] max-md:rounded-b-none dark:border-border dark:bg-card"
        onSubmit={submit}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#e6e6e4] px-4 pt-4 pb-3 dark:border-border">
          <div className="min-w-0">
            <h3
              id="founder-legend-title"
              className="text-[17px] leading-snug font-semibold"
            >
              编辑图例 · {item.label}
            </h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {item.token} · {item.layer} · {item.line_key}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-[#e6e6e4] bg-white px-2 py-1 text-[12px] font-medium text-[#1a4a7a] hover:bg-[#f3f3f1] dark:border-border dark:bg-card dark:text-[#c5d9ef]"
              aria-label="编辑旁查看流程"
              onClick={onOpenFlow}
            >
              流程
            </button>
            <button
              type="button"
              className="size-8 shrink-0 rounded-md border border-[#e6e6e4] bg-white text-muted-foreground hover:bg-[#f3f3f1] dark:border-border dark:bg-card"
              aria-label="关闭弹层"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-auto px-4 py-3 text-[13px]">
          <p className="rounded-md bg-[#f7f7f5] px-3 py-2 text-[12px] text-muted-foreground dark:bg-muted/40">
            可改展示名、描述、颜色、排序、显隐和飞书外链。业务线身份（token /
            layer / line_key）锁定，不能在此删线或把想法升格成业务线。
          </p>

          <Field label="名称" htmlFor="legend-label">
            <Input
              id="legend-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </Field>
          <Field label="描述" htmlFor="legend-description">
            <Textarea
              id="legend-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={3}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="样式" htmlFor="legend-style">
              <select
                id="legend-style"
                className={fieldClass}
                value={visualStyle}
                onChange={(event) =>
                  setVisualStyle(event.target.value as LegendVisualStyle)
                }
              >
                <option value="solid">实心</option>
                <option value="dashed">虚线</option>
              </select>
            </Field>
            <Field label="排序" htmlFor="legend-sort">
              <Input
                id="legend-sort"
                type="number"
                value={sortOrder}
                onChange={(event) => setSortOrder(event.target.value)}
              />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="legend-show"
              checked={showInLegend}
              onCheckedChange={setShowInLegend}
            />
            <Label htmlFor="legend-show">显示在图例</Label>
          </div>

          <ColorField
            id="legend-bar"
            label="月格实心色"
            value={barHex}
            onChange={setBarHex}
          />
          <ColorField
            id="legend-bar-text"
            label="实心字色"
            value={barTextHex}
            onChange={setBarTextHex}
          />
          <ColorField
            id="legend-outline"
            label="虚线描边"
            value={outlineHex}
            onChange={setOutlineHex}
          />
          <ColorField
            id="legend-outline-text"
            label="虚线字色"
            value={outlineTextHex}
            onChange={setOutlineTextHex}
          />
          <ColorField
            id="legend-soft-dot"
            label="周层点色"
            value={softDotHex}
            onChange={setSoftDotHex}
          />
          <ColorField
            id="legend-soft-bg"
            label="周层底色"
            value={softChipBg}
            onChange={setSoftChipBg}
          />
          <ColorField
            id="legend-soft-text"
            label="周层字色"
            value={softChipText}
            onChange={setSoftChipText}
          />

          <Field label="本机账号路径" htmlFor="legend-mac-path">
            <Input
              id="legend-mac-path"
              value={macAccountPath}
              placeholder="账号/观心微语"
              onChange={(event) => setMacAccountPath(event.target.value)}
            />
          </Field>

          <fieldset className="space-y-2">
            <legend className="text-[12px] font-medium text-muted-foreground">
              飞书／外链
            </legend>
            {links.map((link, index) => (
              <div
                key={`${link.url}-${index}`}
                className="grid grid-cols-[7rem_1fr] gap-2 rounded-md border border-[#e6e6e4] p-2 dark:border-border"
              >
                <select
                  aria-label={`链接类型 ${index + 1}`}
                  className={fieldClass}
                  value={link.kind}
                  onChange={(event) =>
                    setLinks((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, kind: event.target.value }
                          : row,
                      ),
                    )
                  }
                >
                  {LEGEND_LINK_KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {LINK_KIND_LABEL[kind]}
                    </option>
                  ))}
                </select>
                <Input
                  aria-label={`链接名称 ${index + 1}`}
                  value={link.label}
                  placeholder="名称"
                  onChange={(event) =>
                    setLinks((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, label: event.target.value }
                          : row,
                      ),
                    )
                  }
                />
                <Input
                  aria-label={`链接地址 ${index + 1}`}
                  className="col-span-2"
                  value={link.url}
                  placeholder="https://my.feishu.cn/…"
                  onChange={(event) =>
                    setLinks((current) =>
                      current.map((row, rowIndex) =>
                        rowIndex === index
                          ? { ...row, url: event.target.value }
                          : row,
                      ),
                    )
                  }
                />
                <button
                  type="button"
                  className="col-span-2 justify-self-end text-[12px] text-[#8B1A1A]"
                  onClick={() =>
                    setLinks((current) =>
                      current.filter((_, rowIndex) => rowIndex !== index),
                    )
                  }
                >
                  删除此链
                </button>
              </div>
            ))}
            <button
              type="button"
              className="text-[13px] font-medium text-[#1a4a7a] dark:text-[#c5d9ef]"
              onClick={() =>
                setLinks((current) => [
                  ...current,
                  { kind: "feishu_wiki", label: "", url: "" },
                ])
              }
            >
              添加链接
            </button>
          </fieldset>
        </div>

        <div className="space-y-2 border-t border-[#e6e6e4] bg-[#f7f7f5] px-4 py-3 dark:border-border dark:bg-muted/40">
          {persistable ? null : (
            <p className="text-[12px] text-muted-foreground">
              当前为本地色标备份，库中尚无图例行，无法保存。
            </p>
          )}
          {error ? (
            <p className="text-[12px] text-[#8B1A1A]" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="rounded-md border border-[#e6e6e4] bg-white px-3 py-1 text-[13px] font-medium text-[#1a4a7a] hover:bg-[#f3f3f1] dark:border-border dark:bg-card dark:text-[#c5d9ef]"
              onClick={onClose}
            >
              关闭
            </button>
            <button
              type="submit"
              disabled={!persistable || busy || !label.trim()}
              className="rounded-md border border-[#1a4a7a] bg-[#1a4a7a] px-3 py-1 text-[13px] font-medium text-white hover:bg-[#153e68] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "保存中…" : "保存"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

const Field = ({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) => (
  <div className="space-y-1">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
  </div>
);

const ColorField = ({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="space-y-1">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label={`${label}取色`}
        className="h-9 w-10 cursor-pointer rounded border border-input bg-transparent p-0.5"
        value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000"}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
      />
      <Input
        id={id}
        value={value}
        placeholder="#000000"
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  </div>
);
